import {
  Credential,
  ServiceProvider,
  StorageFolder,
  UnreachableCaseError,
} from '@uploadcam/common';
import { Dropbox, DropboxAuth } from 'dropbox';
import { AuthProvider } from '../auth/auth.provider';
import { CommonProvider } from '../common/common.provider';
import { StorageAdapter } from './data/common/adapter';
import { StorageManager } from './data/common/manager';
import { DropboxAdapter } from './data/dropbox/dropbox.adapter';
import { GoogleDriveAdapter } from './data/google-drive/google-drive.adapter';
import { OneDriveAdapter } from './data/one-drive/one-drive.adapter';
import { ZohoAdapter } from './data/zoho/zoho.adapter';
import { ZohoApiClient } from './data/zoho/zoho-api.client';
import { OpenStorageFolderInteractor } from './domain/interactors/open-storage-folder.interactor';
import { DialogService } from 'src/app/features/shared/services/dialog.service';
import { OpenStorageInteractor } from './domain/interactors/open-storage.interactor';
import { OpenStorageFileInteractor } from './domain/interactors/open-storage-file.interactor';
import { TeamProvider } from '../team/team.provider';
import { DownloadStorageFileInteractor } from './domain/interactors/download-storage-file.interactor';
import { FeaturesService } from '../../app/features/shared/services/features.service';
import { createTypedPostMessageFn } from '../../app/core/utils';
import { StorageEmbeddedEvent } from './domain/storage-embedded-event';
import { ShowFolderPickerInteractor } from './domain/interactors/show-folder-picker.interactor';
import { ShowMultiFolderPickerInteractor } from './domain/interactors/show-multi-folder-picker.interactor';
import { StorageDialogService } from './storage-dialog.service';
import { ShowAlbumOrSubfolderPickerInteractor } from './domain/interactors/show-album-or-subfolder-picker.interactor';

export abstract class StorageProvider {
  abstract provideDownloadStorageFile(): DownloadStorageFileInteractor;
  abstract provideDropboxAdapter(
    credential: Credential,
    teamRoot?: StorageFolder,
  ): Promise<DropboxAdapter>;
  abstract provideManager(): Promise<StorageManager>;
  abstract provideOpenStorage(): OpenStorageInteractor;
  abstract provideOpenStorageFile(): OpenStorageFileInteractor;
  abstract provideOpenStorageFolder(): OpenStorageFolderInteractor;
  abstract provideShowAlbumOrSubfolderPicker(): ShowAlbumOrSubfolderPickerInteractor;
  abstract provideShowFolderPicker(): ShowFolderPickerInteractor;
  abstract provideShowMultiFolderPicker(): ShowMultiFolderPickerInteractor;
}

export class StorageDefaultProvider implements StorageProvider {
  private readonly postMessage = createTypedPostMessageFn<StorageEmbeddedEvent>();

  constructor(
    private readonly auth: AuthProvider,
    private readonly common: CommonProvider,
    private readonly dialog: DialogService,
    private readonly features: FeaturesService,
    private readonly team: TeamProvider,
  ) {}

  private async provideAdapter(
    credential: Credential,
    teamRoot?: StorageFolder,
  ): Promise<StorageAdapter> {
    switch (credential.provider) {
      case ServiceProvider.Dropbox:
        return this.provideDropboxAdapter(credential, teamRoot);

      case ServiceProvider.Google:
        const googleService = this.common.provideGoogleApiService();

        if (!googleService.isLoaded()) {
          await googleService.load();
        }

        gapi.client.setToken({ access_token: credential.accessToken });

        return new GoogleDriveAdapter(
          this.auth.provideGetCredential(),
          gapi,
          this.common.provideLogger().withTag('GoogleDriveAdapter'),
          teamRoot,
        );

      case ServiceProvider.Microsoft:
        return new OneDriveAdapter(
          credential,
          this.common.provideLogger().withTag('OneDriveAdapter'),
          teamRoot,
        );

      case ServiceProvider.Zoho:
        return new ZohoAdapter(
          new ZohoApiClient(),
          this.auth.provideGetAuthDto(),
          this.common.provideLogger().withTag('ZohoAdapter'),
        );

      default:
        throw new UnreachableCaseError(credential.provider);
    }
  }

  public async provideDropboxAdapter(
    credential: Credential,
    teamRoot?: StorageFolder,
  ): Promise<DropboxAdapter> {
    const dbxAuth = new DropboxAuth({ accessToken: credential.accessToken });
    let isTeam = false;
    let dbx = new Dropbox({ auth: dbxAuth });

    // Check if the user is part of a team
    const account = await dbx.usersGetCurrentAccount();

    if (account.result.team) {
      isTeam = true;
      dbx = new Dropbox({
        auth: dbxAuth,
        pathRoot: JSON.stringify({
          '.tag': 'root',
          root: account.result.root_info.root_namespace_id,
        }),
      });
    }

    return new DropboxAdapter(
      dbx,
      dbxAuth,
      this.common.provideLogger().withTag('DropboxAdapter'),
      isTeam,
      teamRoot,
    );
  }

  public async provideManager(): Promise<StorageManager> {
    let credential = this.auth.provideGetCredential().execute();

    if (credential.isExpired) {
      credential = await this.auth.provideRefreshCredential().execute(credential);
    }

    const user = await this.auth.provideGetUser().execute();
    let folder: StorageFolder | undefined;

    if (user.teamId) {
      const team = await this.team.provideGetTeamById().execute(user.teamId);
      folder = team.folder;
    }

    const adapter = await this.provideAdapter(credential, folder);

    return new StorageManager(adapter, credential, this.auth.provideRefreshCredential());
  }

  public provideDownloadStorageFile(): DownloadStorageFileInteractor {
    return new DownloadStorageFileInteractor(
      this.dialog,
      this.features,
      this.common.provideLogger().withTag('DownloadStorageFileInteractor'),
      this.postMessage,
      this,
    );
  }

  public provideOpenStorage(): OpenStorageInteractor {
    return new OpenStorageInteractor(
      this.dialog,
      this.common.provideLogger().withTag('OpenStorageInteractor'),
      this,
    );
  }

  public provideOpenStorageFile(): OpenStorageFileInteractor {
    return new OpenStorageFileInteractor(
      this.dialog,
      this.features,
      this.common.provideLogger().withTag('OpenStorageFileInteractor'),
      this.postMessage,
      this,
    );
  }

  public provideOpenStorageFolder(): OpenStorageFolderInteractor {
    return new OpenStorageFolderInteractor(
      this.dialog,
      this.features,
      this.common.provideLogger().withTag('OpenStorageFolderInteractor'),
      this.postMessage,
      this,
    );
  }

  public provideShowAlbumOrSubfolderPicker(): ShowAlbumOrSubfolderPickerInteractor {
    return new ShowAlbumOrSubfolderPickerInteractor(
      this.auth.provideGetCredential(),
      new StorageDialogService(this.dialog),
      this,
    );
  }

  public provideShowFolderPicker(): ShowFolderPickerInteractor {
    return new ShowFolderPickerInteractor(
      this.dialog,
      this.auth.provideGetCredential(),
      this.common.provideGoogleApiService(),
      this.auth.provideRefreshCredential(),
      new StorageDialogService(this.dialog),
      this,
    );
  }

  public provideShowMultiFolderPicker(): ShowMultiFolderPickerInteractor {
    return new ShowMultiFolderPickerInteractor(
      this.auth.provideGetCredential(),
      new StorageDialogService(this.dialog),
      this,
    );
  }
}
